import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@project/ui";
import { Button } from "@project/ui";
import { formatShort } from "@project/ui";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { useActiveHouse } from "@apps/dashboard/features/houses/active-house-context";
import { useSyncHouseFromParams } from "@apps/dashboard/features/houses/use-sync-house-from-params";
import {
  deleteVaultItem,
  fetchVaultItem,
  homeExpenseQueryKeys,
  openStorageDocumentInNewTab,
  patchVaultItem,
} from "@apps/dashboard/lib/home-expense-api";
import { FilePondStorageUpload } from "@apps/dashboard/components/FilePondStorageUpload";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import {
  VAULT_CATEGORY_SLUGS,
  vaultCategoryDisplayLabel,
} from "@apps/dashboard/lib/vault-ui";
import { DashboardBackLink } from "@apps/dashboard/components/layout/DashboardBackLink";
import { DASHBOARD_PAGE_CONTAINER } from "@apps/dashboard/components/layout/dashboard-page-layout";
import { ErrorState } from "@apps/dashboard/components/states/ErrorState";
import { LoadingState } from "@apps/dashboard/components/states/LoadingState";
import { Icon, cn } from "@project/ui";

export default function VaultDetailPage(): JSX.Element {
  const { houseId, itemId } = useParams<{ houseId: string; itemId: string }>();
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { houses } = useActiveHouse();
  const actorRole = houseId ? houses?.find((h) => h.id === houseId)?.role : undefined;
  const canEdit = actorRole === "owner" || actorRole === "admin";
  useSyncHouseFromParams(houseId);

  const itemQuery = useQuery({
    queryKey:
      houseId && itemId
        ? homeExpenseQueryKeys.vaultItem(locale, houseId, itemId)
        : ["vaultItem", "noop"],
    queryFn: () => {
      if (!houseId || !itemId) throw new Error("missing id");
      return fetchVaultItem(locale, houseId, itemId);
    },
    enabled: Boolean(houseId && itemId),
  });

  const [name, setName] = useState("");
  const [assetType, setAssetType] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");
  const [warrantyEndsOn, setWarrantyEndsOn] = useState("");
  const [notes, setNotes] = useState("");
  const [documentStorageKey, setDocumentStorageKey] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    const row = itemQuery.data;
    if (!row) return;
    setName(row.name);
    setAssetType(row.assetType ?? "");
    setPurchaseDate(row.purchaseDate ?? "");
    setWarrantyEndsOn(row.warrantyEndsOn ?? "");
    setNotes(row.notes ?? "");
    setDocumentStorageKey(row.documentStorageKey ?? "");
  }, [itemQuery.data]);

  const patchMutation = useMutation({
    mutationFn: () => {
      if (!houseId || !itemId) throw new Error("missing id");
      return patchVaultItem(locale, houseId, itemId, {
        name: name.trim(),
        assetType: assetType.trim() || null,
        purchaseDate: purchaseDate || null,
        warrantyEndsOn: warrantyEndsOn || null,
        notes: notes.trim() || null,
        documentStorageKey: documentStorageKey.trim() || null,
      });
    },
    onSuccess: async () => {
      if (houseId && itemId) {
        await queryClient.invalidateQueries({
          queryKey: homeExpenseQueryKeys.vaultItem(locale, houseId, itemId),
        });
        await queryClient.invalidateQueries({ queryKey: homeExpenseQueryKeys.vault(locale, houseId) });
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => {
      if (!houseId || !itemId) throw new Error("missing id");
      return deleteVaultItem(locale, houseId, itemId);
    },
    onSuccess: async () => {
      if (houseId) {
        await queryClient.invalidateQueries({ queryKey: homeExpenseQueryKeys.vault(locale, houseId) });
      }
      navigate(dashboardPaths.houseVault(houseId!), { replace: true });
    },
  });

  if (!houseId || !itemId) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <ErrorState />
      </div>
    );
  }

  const categoryLabel =
    itemQuery.data && !itemQuery.isLoading && !itemQuery.isError
      ? vaultCategoryDisplayLabel(t, itemQuery.data.assetType)
      : null;

  return (
    <div className={cn(DASHBOARD_PAGE_CONTAINER, "space-y-8")}>
      <DashboardBackLink to={dashboardPaths.houseVault(houseId)}>{t("dashboardUi.vault.backToList")}</DashboardBackLink>
      <h1 className="font-headline text-headline-sm font-bold text-primary">{t("dashboardUi.pages.vaultDetail")}</h1>

      {itemQuery.isLoading ? (
        <LoadingState />
      ) : itemQuery.isError ? (
        <ErrorState onRetry={() => void itemQuery.refetch()} />
      ) : (
        <div className="space-y-6">
          <div className="rounded-2xl border border-outline-variant/15 bg-surface-container-low p-6">
            <div className="flex size-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <Icon name="Lock" className="size-7" strokeWidth={1.5} />
            </div>
            <dl className="mt-4 space-y-2 text-body-md text-on-surface-variant">
              {categoryLabel ? (
                <div>
                  <dt className="text-label-md">{t("dashboardUi.vault.assetTypeLabel")}</dt>
                  <dd className="text-on-surface">{categoryLabel}</dd>
                </div>
              ) : null}
              {itemQuery.data?.purchaseDate ? (
                <div>
                  <dt className="text-label-md">{t("dashboardUi.vault.purchaseDateLabel")}</dt>
                  <dd className="text-on-surface">
                    {formatShort(`${itemQuery.data.purchaseDate}T12:00:00.000Z`)}
                  </dd>
                </div>
              ) : null}
              {itemQuery.data?.warrantyEndsOn ? (
                <div>
                  <dt className="text-label-md">{t("dashboardUi.vault.warrantyLabel")}</dt>
                  <dd className="text-on-surface">
                    {formatShort(`${itemQuery.data.warrantyEndsOn}T12:00:00.000Z`)}
                  </dd>
                </div>
              ) : null}
            </dl>
            {itemQuery.data?.documentStorageKey ? (
              <Button
                type="button"
                variant="outline"
                className="mt-6 w-full gap-2 border-primary/30 text-primary"
                onClick={() => {
                  const key = itemQuery.data?.documentStorageKey;
                  if (!key) return;
                  void openStorageDocumentInNewTab(locale, key).catch(() => {
                    window.alert(t("dashboardUi.states.errorHint"));
                  });
                }}
              >
                <Icon name="FileText" className="size-4" strokeWidth={1.5} />
                {t("dashboardUi.vault.viewDocument")}
              </Button>
            ) : null}
          </div>

          {canEdit ? (
            <form
              className="space-y-5 rounded-2xl border border-outline-variant/15 bg-surface-container-lowest p-6"
              onSubmit={(e) => {
                e.preventDefault();
                setFormError(null);
                if (!name.trim()) {
                  setFormError(t("validation.required"));
                  return;
                }
                void patchMutation.mutateAsync().catch((err: unknown) => {
                  setFormError(err instanceof Error ? err.message : t("dashboardUi.states.errorHint"));
                });
              }}
            >
              <label className="block">
                <span className="text-label-md text-on-surface-variant">{t("dashboardUi.vault.nameLabel")}</span>
                <input
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-3 py-2 font-body text-on-surface"
                />
              </label>
              <label className="block">
                <span className="text-label-md text-on-surface-variant">{t("dashboardUi.vault.assetTypeLabel")}</span>
                <div className="relative mt-1">
                  <select
                    id="vault-detail-category"
                    value={assetType}
                    onChange={(e) => setAssetType(e.target.value)}
                    className="w-full appearance-none rounded-xl border border-outline-variant/30 bg-surface px-3 py-2 pr-10 font-body text-on-surface"
                  >
                    <option value="">{t("dashboardUi.vault.categoryPlaceholder")}</option>
                    {VAULT_CATEGORY_SLUGS.map((slug) => (
                      <option key={slug} value={slug}>
                        {t(`dashboardUi.vault.categories.${slug}`)}
                      </option>
                    ))}
                  </select>
                  <Icon
                    name="ChevronDown"
                    className="pointer-events-none absolute right-3 top-1/2 size-5 -translate-y-1/2 text-on-surface-variant"
                    strokeWidth={1.5}
                  />
                </div>
              </label>
              <label className="block">
                <span className="text-label-md text-on-surface-variant">
                  {t("dashboardUi.vault.purchaseDateLabel")}
                </span>
                <input
                  type="date"
                  value={purchaseDate}
                  onChange={(e) => setPurchaseDate(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-3 py-2 font-body text-on-surface"
                />
              </label>
              <label className="block">
                <span className="text-label-md text-on-surface-variant">{t("dashboardUi.vault.warrantyLabel")}</span>
                <input
                  type="date"
                  value={warrantyEndsOn}
                  onChange={(e) => setWarrantyEndsOn(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-3 py-2 font-body text-on-surface"
                />
              </label>
              <label className="block">
                <span className="text-label-md text-on-surface-variant">{t("dashboardUi.vault.notesLabel")}</span>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-3 py-2 font-body text-on-surface"
                />
              </label>
              <div className="space-y-2">
                <span className="text-label-md text-on-surface-variant">{t("dashboardUi.vault.documentKeyLabel")}</span>
                <input
                  value={documentStorageKey}
                  onChange={(e) => setDocumentStorageKey(e.target.value)}
                  className="w-full rounded-xl border border-outline-variant/30 bg-surface px-3 py-2 font-body text-sm text-on-surface"
                />
                <FilePondStorageUpload
                  locale={locale}
                  mode="vault"
                  maxFiles={1}
                  inputId="vault-detail-filepond"
                  className="filepond-vault-upload w-full rounded-xl border border-outline-variant/30 bg-surface-container-low/50 p-2"
                  onStoredKeys={(keys) => {
                    const k = keys[keys.length - 1];
                    if (k) {
                      setFormError(null);
                      setDocumentStorageKey(k);
                    }
                  }}
                />
              </div>
              {formError ? <p className="text-body-md text-error">{formError}</p> : null}
              <Button
                type="submit"
                className="primary-gradient w-full rounded-xl text-on-primary"
                disabled={patchMutation.isPending}
              >
                {patchMutation.isPending ? t("dashboardUi.vault.saving") : t("dashboardUi.vault.saveCta")}
              </Button>
            </form>
          ) : (
            <p className="text-body-md text-on-surface-variant">{t("dashboardUi.vault.readOnlyHint")}</p>
          )}

          {canEdit ? (
            <Button
              type="button"
              variant="outline"
              className="w-full border-error/40 text-error hover:bg-error-container/20"
              disabled={deleteMutation.isPending}
              onClick={() => {
                if (window.confirm(t("dashboardUi.vault.deleteConfirm"))) {
                  void deleteMutation.mutateAsync().catch(() => undefined);
                }
              }}
            >
              {t("dashboardUi.vault.deleteCta")}
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
}
